import { memo, useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { CollapsibleSection } from '@/components/atoms/CollapsibleSection/CollapsibleSection';
import { integrationIconByType } from '@/components/atoms/Icons/integrationIconByType';
import { MODAL_NEW_MESSAGE } from '@/components/organisms/Modals/ModalConductor';
import { Button } from '@/components/ui/button';
import { useDeskId } from '@/hooks/useDeskId';
import { useIntegrations } from '@/hooks/useIntegrations';
import { conversationDetailKey } from '@/modules/humanChat/hooks/useConversations';
import { pushModal } from '@/redux/modals/actions';
import { selectSessionId } from '@/redux/session/selectors';
import { Conversation } from '@/shared/types/chat';
import { IntegrationStatus, IntegrationType } from '@/shared/types/integration';
import { getChannelKey } from '@/util/channelName';
import { DIRECT_MESSAGE_INTEGRATION_TYPES, getRecipientType } from '@/util/outboundIntegrations';

export const FollowUpsSection = memo(() => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const deskId = useDeskId();
  const sessionId = useSelector(selectSessionId);
  const queryClient = useQueryClient();
  const { integrations: allIntegrations } = useIntegrations(deskId || '');

  const followUpIntegrations = (allIntegrations ?? []).filter(
    (integration) =>
      DIRECT_MESSAGE_INTEGRATION_TYPES.includes(integration.type as IntegrationType) &&
      integration.active &&
      integration.status === IntegrationStatus.VERIFIED
  );

  const handleFollowUp = useCallback(
    (integrationId: string, integrationType: IntegrationType) => {
      // Read at click time: subscribing would re-render on every conversation cache write.
      const userContext = queryClient.getQueryData<Conversation>(
        conversationDetailKey(deskId, sessionId)
      )?.context?.user;
      const recipientType = getRecipientType(integrationType);
      const initialTo =
        recipientType === 'email' ? userContext?.email || '' : userContext?.phone || '';
      dispatch(pushModal(MODAL_NEW_MESSAGE, { initialTo, initialIntegrationId: integrationId }));
    },
    [queryClient, deskId, sessionId, dispatch]
  );

  if (followUpIntegrations.length === 0) {
    return null;
  }

  return (
    <section>
      <CollapsibleSection
        title={t('common.direct_message')}
        showBorder={false}
        storageKey="humanChat.sidebar.followUps"
      >
        <ul className="flex flex-col gap-px pb-3">
          {followUpIntegrations.map((integration) => {
            const Icon = integrationIconByType[integration.type as IntegrationType];
            return (
              <li key={integration.integration_id}>
                <Button
                  variant="ghost-neutral"
                  className="h-9 w-full justify-start gap-1.5 px-1.5"
                  onClick={() =>
                    handleFollowUp(integration.integration_id, integration.type as IntegrationType)
                  }
                >
                  {Icon && <Icon size={16} className="shrink-0" />}
                  <span className="truncate text-sm leading-5 font-normal text-foreground">
                    {t(getChannelKey(integration.type as IntegrationType))}
                  </span>
                </Button>
              </li>
            );
          })}
        </ul>
      </CollapsibleSection>
    </section>
  );
});

FollowUpsSection.displayName = 'FollowUpsSection';
